/**
 * useSignInThrottle.js
 *
 * React wrapper around signInThrottle with a live 1-second countdown while blocked,
 * so the cooldown notice ticks down and auto-clears. Re-reads localStorage each tick,
 * so attempts made in OTHER tabs are reflected within ~1s.
 *
 * @param {boolean} active - poll while true (e.g. the sign-in modal is open)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getThrottleState, recordAttempt, clearAttempts } from '../utils/signInThrottle';
import { track } from '../utils/authTelemetry';

export default function useSignInThrottle(active) {
  const [state, setState] = useState(() => getThrottleState());
  const reportedRef = useRef(false); // fire the analytics event once per rate-block episode

  useEffect(() => {
    if (!active) return undefined;
    const tick = () => {
      const next = getThrottleState();
      // Telemetry: emit once when the guard starts rate-blocking, so we can watch how
      // often users hit it (a proxy for the underlying session-not-sticking problem).
      // Plausible (already loaded) keeps this off the unauthenticated DataStore surface.
      if (next.blocked && next.reason === 'rate' && !reportedRef.current) {
        reportedRef.current = true;
        track('SignInThrottled');
      }
      if (!next.blocked) reportedRef.current = false;
      setState(next);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);

  const record = useCallback(() => {
    recordAttempt();
    setState(getThrottleState());
  }, []);

  const clear = useCallback(() => {
    clearAttempts();
    setState(getThrottleState());
  }, []);

  return {
    blocked: state.blocked,
    reason: state.reason,
    secondsRemaining: Math.ceil(state.retryInMs / 1000),
    recordAttempt: record,
    clearAttempts: clear,
  };
}
